import { isString } from '@summit/core';
import { GetQuickFilterTextParams, RowDragCallbackParams } from 'ag-grid-enterprise';

import { DisplayType } from './display-type.enum';
import { IDisplaySchema } from './i-display-schema';
import { SummitFormattedText } from './summit-formatted-text';
import { SummitIdObject } from './summit-id-object';
import { ViewField } from './view/view-field';

export class SummitGridCallbacks {
  static getQuickFilterText<T extends SummitIdObject>(
    groupIndex: number,
    columnFilterActive: (keyof T)[],
    schema: IDisplaySchema<T>,
    attribute: ViewField<T>
  ): (params: GetQuickFilterTextParams) => string | undefined {
    return (params): string | undefined => {
      if (columnFilterActive.indexOf(attribute.propertyName) < 0) return '';
      const value =
        groupIndex >= 0
          ? params.data
            ? schema.getTextLabelLong(params.data, attribute)
            : null
          : params.data
            ? schema.getTextLabel(params.data, attribute)
            : null;
      const reFormatedValue = isString(value) ? value.replace(/,/g, '') : value;
      if (value === reFormatedValue) return value?.toString();
      return `${value}----${reFormatedValue}`;
    };
  }

  static isRowDraggable = (params: RowDragCallbackParams): boolean => {
    // only rows that are NOT groups should be draggable
    return !params.node.group;
  };
  static sortByParameter<T>(property: keyof T, a: T, b: T) {
    if (a[property] === b[property]) return 0;
    return a[property] > b[property] ? 1 : -1;
  }

  static ValueGetter<T extends SummitIdObject>(
    schema: IDisplaySchema<T>,
    attribute: ViewField<T>,
    params: any
  ): unknown {
    if (!attribute) return params.value;
    if (attribute.valueGetterOverride) {
      const override = attribute.valueGetterOverride(params);
      if (override !== undefined) {
        //console.log('Returning Override', override);
        return override;
      }
    }
    const baseValue = params.data ? params.data[attribute.propertyName] : null;
    switch (attribute.displayType) {
      case DisplayType.ShortStringArrayFT: {
        if (Array.isArray(baseValue)) {
          const response = new SummitFormattedText();
          baseValue
            .map((entry) => (entry?.toShortString ? entry.toShortString() : entry))
            .filter((entry) => entry !== null)
            .forEach((entry) => response.AddLine(entry));
          return response;
        }
        break;
      }
      case DisplayType.ShortStringArrayWithParameterFT: {
        if (Array.isArray(baseValue)) {
          const response = new SummitFormattedText();
          baseValue
            .map((entry) =>
              entry?.toShortString ? entry.toShortString(params.data[attribute.displayTypeParameter as string]) : entry
            )
            .filter((entry) => entry !== null)
            .forEach((entry) => response.AddLine(entry));
          return response;
        }
        break;
      }
      default:
        break;
    }
    //For pinned footer don't display null next values
    if (params.node.id == 'bottom') return baseValue;
    return baseValue || baseValue == 0 ? baseValue : schema.getTextLabel(params.data, attribute);
  }

  //TODO this shouldn't be needed, need to separate value from formatter in overflow.
  static ValueGetterForOverflow<T extends SummitIdObject>(
    schema: IDisplaySchema<T>,
    attribute: ViewField<T>,
    params: any
  ): unknown {
    const value = SummitGridCallbacks.ValueGetter(schema, attribute, params);
    if ((value as SummitFormattedText)?.isFormattedText) return value;
    //if (attribute.propertyName == 'gender') console.log('ValueGetterForOverflow', value);
    return schema.getTextLabel(params.data, attribute);
  }
}
